import React from 'react';
import { MsalProvider } from '@azure/msal-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { msalInstance } from './lib/msal';
import { SessionProvider, useSession } from './lib/session';
import { LocationProvider } from './lib/location';
import { ToastProvider } from './lib/toast';
import { LayoutProvider } from './lib/layout';
import { AuthGuard } from './components/AuthGuard';
import { AppHeader } from './components/AppHeader';
import { Home } from './pages/Home';
import { TapsPage } from './pages/TapsPage';
import { BeersPage } from './pages/BeersPage';
import { NewBeerPage } from './pages/NewBeerPage';
import { AdminPage } from './pages/AdminPage';
import { BartendersPage } from './pages/BartendersPage';
import { EventsPage } from './pages/EventsPage';
import { ReservationsPage } from './pages/ReservationsPage';
import { KioskPage } from './pages/KioskPage';
import { TapHistoryPage } from './pages/TapHistoryPage';
import { TimecardsPage } from './pages/TimecardsPage';
import { KIOSK_ROLES, TIMECARD_ROLES } from './lib/roles';

// ---------------------------------------------------------------------------
// React Query client
// ---------------------------------------------------------------------------

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------

export default function App(): React.ReactElement {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <LayoutProvider>
          <AuthGuard>
            <SessionProvider>
              <LocationProvider>
                <ToastProvider>
                  <ShellSelector />
                </ToastProvider>
              </LocationProvider>
            </SessionProvider>
          </AuthGuard>
          </LayoutProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </MsalProvider>
  );
}

// ---------------------------------------------------------------------------
// Shell selector — renders the kiosk shell for BarKiosk accounts,
// the normal app shell for everyone else.
// ---------------------------------------------------------------------------

function ShellSelector(): React.ReactElement {
  const { hasRole } = useSession();
  if (hasRole(...KIOSK_ROLES)) return <KioskShell />;
  return <AppShell />;
}

// ---------------------------------------------------------------------------
// Kiosk shell — full-viewport, no nav, no header chrome
// ---------------------------------------------------------------------------

function KioskShell(): React.ReactElement {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100dvh', overflow: 'hidden' }}>
      <KioskPage />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Normal app shell
// ---------------------------------------------------------------------------

function AppShell(): React.ReactElement {
  const { hasRole } = useSession();
  return (
    <div className="app-shell">
      <AppHeader />
      <main className="app-main">
        <Routes>
          <Route path="/"           element={<Home />} />
          <Route path="/taps"       element={<TapsPage />} />
          <Route path="/tap-history" element={<TapHistoryPage />} />
          <Route path="/beers"      element={<BeersPage />} />
          <Route path="/beers/new"  element={<NewBeerPage />} />
          <Route path="/ops"        element={<AdminPage />} />
          <Route path="/bartenders" element={<BartendersPage />} />
          <Route path="/events"         element={<EventsPage />} />
          <Route path="/reservations"   element={<ReservationsPage />} />
          {hasRole(...TIMECARD_ROLES) && (
            <Route path="/timecards" element={<TimecardsPage />} />
          )}
          <Route path="/*"          element={<Home />} />
        </Routes>
      </main>
    </div>
  );
}
